import React, { useEffect, useRef, useState } from 'react';
import { IOrderDetail, IOrderDetailLine, IOrderDetailLineLegs } from '../../../actions/order-details/get-order-details.action';
import { NO_DATA_FALLBACK } from '../../../configuration';
import {
    DELIVERY_INFORMATION_LEG_STATES,
    DELIVERY_INFORMATION_LEG_TYPES,
    DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS
} from '../../../configuration/delivery-information';
import { ILocalizationState } from '../../../states/localization-state';
import { dateLocaleFormatter, toLocalDate } from '../../../utilities';
import { mapEventNameToText } from '../../../utilities/map-event-name-to-text';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';
import { DefaultLeg } from './delivery-information-modal';

interface EventProgressBarProps {
    orderLine: IOrderDetailLine;
    CLASS_NAME: string;
    resources: IOrderDetailsResources;
    localizationState: ILocalizationState;
    orderDetails: IOrderDetail;
    createStaticLeg: (id: number, name: string, icon: string, timestamp?: string) => DefaultLeg;
}

const getShipmentProgress = (
    orderLine: IOrderDetailLine,
    orderDetails: IOrderDetail,
    createStaticLeg: (id: number, name: string, icon: string, timestamp?: string) => DefaultLeg
) => {
    const { Legs } = orderLine;
    const ordered = createStaticLeg(
        0,
        DELIVERY_INFORMATION_LEG_STATES.Ordered,
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.PARCEL,
        orderDetails.ConfirmedDate
    );
    const completed = createStaticLeg(
        Legs.length + 1,
        DELIVERY_INFORMATION_LEG_STATES.Complete,
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.FLAG,
        orderLine.ActualArrivalDate ? orderLine.ActualArrivalDate : undefined
    );

    const preCarriageLegs = Legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.PreCarriage);
    const mainCarriageLegs = Legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.MainCarriage);
    const onCarriageLegs = Legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.OnCarriage);

    const completedLegsIndices = Legs.map((leg, index) =>
        leg.State === DELIVERY_INFORMATION_LEG_STATES.Active || leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed ? index : -1
    ).filter(index => index !== -1);

    const allLegsCompleted = Legs.every(leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed);

    const activeStep = allLegsCompleted
        ? Legs.length + 1
        : completedLegsIndices.length > 0
        ? completedLegsIndices[completedLegsIndices.length - 1] + 1
        : 0;

    return {
        preCarriageLegs,
        mainCarriageLegs,
        onCarriageLegs,
        ordered,
        completed,
        activeStep,
        allLegsCompleted
    };
};

const renderEvent = (
    leg: IOrderDetailLineLegs | DefaultLeg,
    activeStep: number,
    localizationState: ILocalizationState,
    CLASS_NAME: string,
    resources: IOrderDetailsResources,
    ref?: React.Ref<HTMLDivElement>
) => {
    const lastEvent = leg.Events?.[leg.Events?.length - 1];

    const mode = leg.ModeOfTransportation?.toUpperCase();
    // eslint-disable-next-line security/detect-object-injection
    const iconType = DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS[mode] || 'truck';

    return (
        <div className={`${CLASS_NAME}__event`} key={leg.Id}>
            <div
                ref={ref}
                className={`${CLASS_NAME}__event-bubble ${leg.Id === activeStep ? 'active' : ''} ${leg.Id > activeStep ? 'open' : ''}`}
            >
                <span className={`${CLASS_NAME}__event-bubble__icon-${iconType}`} />
            </div>
            <div className={`${CLASS_NAME}__event-description`}>
                <strong>{leg.Id <= activeStep ? mapEventNameToText(lastEvent?.Name, resources) : NO_DATA_FALLBACK}</strong>
                <p>
                    {leg.Id <= activeStep && lastEvent?.Timestamp
                        ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(lastEvent?.Timestamp))
                        : NO_DATA_FALLBACK}
                </p>
                <p>{lastEvent?.Location}</p>
            </div>
        </div>
    );
};

const getCurrentActiveLeg = (legs: IOrderDetailLineLegs[]) => {
    if (legs?.length === 0) {
        return null;
    }
    const activeOrCompletedLegs = legs.filter(
        leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Active || leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed
    );
    return activeOrCompletedLegs.length > 0 ? activeOrCompletedLegs[activeOrCompletedLegs.length - 1] : legs[0];
};

const EventProgressBar: React.FC<EventProgressBarProps> = ({
    CLASS_NAME,
    orderLine,
    localizationState,
    orderDetails,
    createStaticLeg,
    resources
}) => {
    const { preCarriageLegs, mainCarriageLegs, onCarriageLegs, ordered, completed, activeStep, allLegsCompleted } = getShipmentProgress(
        orderLine,
        orderDetails,
        createStaticLeg
    );

    const firstBubbleRef = useRef<HTMLDivElement | null>(null);
    const activeBubbleRef = useRef<HTMLDivElement | null>(null);
    const lastBubbleRef = useRef<HTMLDivElement | null>(null);
    const [activeLineWidth, setActiveLineWidth] = useState(0);
    const [defaultLineWidth, setDefaultLineWidth] = useState(0);
    const [defaultLineOffset, setDefaultLineOffset] = useState(0);

    //Calculate width of progress bar line
    useEffect(() => {
        if (firstBubbleRef.current && lastBubbleRef.current) {
            const startOffset = firstBubbleRef.current.offsetLeft;
            const endOffset = lastBubbleRef.current.offsetLeft;
            setDefaultLineOffset(startOffset);
            setDefaultLineWidth(endOffset - startOffset);

            if (allLegsCompleted) {
                setActiveLineWidth(endOffset - startOffset);
            } else if (!allLegsCompleted && activeBubbleRef.current) {
                const activeOffset = activeBubbleRef.current.offsetLeft;
                setActiveLineWidth(activeOffset - startOffset);
            }
        }
    }, [firstBubbleRef, activeBubbleRef, lastBubbleRef, allLegsCompleted]);

    const currentPreCarriageLeg = getCurrentActiveLeg(preCarriageLegs);
    const currentMainCarriageLeg = getCurrentActiveLeg(mainCarriageLegs);
    const currentOnCarriageLeg = getCurrentActiveLeg(onCarriageLegs);

    return (
        <div className={`${CLASS_NAME}__section fade-slide-right`}>
            <div
                className={`${CLASS_NAME}__progress-steps__default-line`}
                style={{ width: defaultLineWidth, left: defaultLineOffset + 1 }}
            ></div>
            {ordered && renderEvent(ordered, activeStep, localizationState, CLASS_NAME, resources, firstBubbleRef)}
            {currentPreCarriageLeg &&
                renderEvent(
                    currentPreCarriageLeg,
                    activeStep,
                    localizationState,
                    CLASS_NAME,
                    resources,
                    currentPreCarriageLeg.Id === activeStep ? activeBubbleRef : null
                )}
            {currentMainCarriageLeg &&
                renderEvent(
                    currentMainCarriageLeg,
                    activeStep,
                    localizationState,
                    CLASS_NAME,
                    resources,
                    currentMainCarriageLeg.Id === activeStep ? activeBubbleRef : null
                )}
            {currentOnCarriageLeg &&
                renderEvent(
                    currentOnCarriageLeg,
                    activeStep,
                    localizationState,
                    CLASS_NAME,
                    resources,
                    currentOnCarriageLeg.Id === activeStep ? activeBubbleRef : null
                )}
            {completed && renderEvent(completed, activeStep, localizationState, CLASS_NAME, resources, lastBubbleRef)}
            <div
                className={`${CLASS_NAME}__progress-steps__filled-line`}
                style={{ width: activeLineWidth, left: defaultLineOffset + 1 }}
            ></div>
        </div>
    );
};

export default EventProgressBar;
